import { inject, Injectable, signal } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { HelpTopic, resolveHelpTopic } from "./help-topics";

/** What the help panel is showing: a public manual page, at a section or from the top. */
export interface HelpTarget {
  page: string;
  section: string | null;
}

@Injectable({ providedIn: "root" })
/**
 * State of the in-app help panel (work item 830). The panel opens on the current screen's own
 * section and, while it is open, follows the reader from screen to screen.
 */
export class HelpPanelService {
  private readonly router = inject(Router);

  readonly isOpen = signal(false);
  /** The current screen's help: its title, own section and related sections. */
  readonly topic = signal<HelpTopic>(resolveHelpTopic(this.router.url));
  /** The section on screen, which differs from the topic's once the reader follows a link. */
  readonly target = signal<HelpTarget>(targetOf(this.topic()));

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const topic = resolveHelpTopic(event.urlAfterRedirects);
        this.topic.set(topic);
        if (this.isOpen()) this.target.set(targetOf(topic));
      });
  }

  /** Opens the help for the screen the reader is on. */
  openForCurrentScreen(): void {
    const topic = resolveHelpTopic(this.router.url);
    this.topic.set(topic);
    this.target.set(targetOf(topic));
    this.isOpen.set(true);
  }

  /** Shows a particular section, for a related link or a cross-reference. */
  show(page: string, section: string | null): void {
    this.target.set({ page, section });
    this.isOpen.set(true);
  }

  /** Back to the current screen's own section. */
  showTopic(): void {
    this.target.set(targetOf(this.topic()));
  }

  close(): void {
    this.isOpen.set(false);
  }

  toggle(): void {
    if (this.isOpen()) this.close();
    else this.openForCurrentScreen();
  }

  /** Whether the panel shows the screen's own section rather than one reached by a link. */
  isShowingTopic(): boolean {
    const topic = this.topic();
    const target = this.target();
    return target.page === topic.page && target.section === topic.section;
  }
}

function targetOf(topic: HelpTopic): HelpTarget {
  return { page: topic.page, section: topic.section };
}
